import { useCallback, useRef } from 'react';

import { useCoreIntersectionObserver } from './useIntersectionObserver';

/**
 * Observes the visibility of a DOM element via the Intersection Observer API,
 * and invokes a callback when the element becomes visible (intersecting).
 *
 * ✅ Use when:
 * - You want to perform a side effect (e.g., analytics, lazy-loading, animations)
 *   when an element becomes visible in the viewport.
 * - You do NOT want to trigger a React rerender based on intersection.
 *
 * ⚠️ Caveats:
 * - The `ref` must point to a stable element (i.e., not `null` during initial render).
 * - The callback should be memoized (`useCallback`) if defined inline, to avoid re-subscribing on every render.
 * - For "only once" behavior, set `onlyOnce = true` (default); otherwise, the callback may fire multiple times.
 *
 * @param onIntersect - Callback invoked when the element becomes visible
 * @param onlyOnce - Whether to fire the callback only on the first intersection
 * @param options - IntersectionObserver options (threshold, root, etc.)
 * @returns React.RefObject<HTMLElement> - Ref to attach to the target element
 */

export default function useOnIntersect(
  onIntersect: () => void,
  onlyOnce: boolean,
  options?: IntersectionObserverInit
): React.RefObject<HTMLElement> {
  const ref = useRef<HTMLElement>(null);
  const hasFired = useRef(false);

  const handleIntersect = useCallback(
    ([entry]: IntersectionObserverEntry[]) => {
      if (entry.isIntersecting && (!onlyOnce || !hasFired.current)) {
        hasFired.current = true;
        onIntersect();
      }
    },
    [onIntersect, onlyOnce]
  );

  useCoreIntersectionObserver(ref, handleIntersect, options);

  return ref;
}
